import { cn } from '@/lib/utils';
import { useEffect, useRef } from 'react';

import { useIdleFillMessage } from './useIdleFillMessage';

interface ProgressStep {
    label: string;
    active: boolean;
}

interface Props {
    steps: ProgressStep[];
}

const ROW_GAP_REM = 0.75;

function ThinkingStepRow({
    step,
    index,
    isLast,
    isNew
}: {
    step: ProgressStep;
    index: number;
    isLast: boolean;
    isNew: boolean;
}) {
    const displayLabel = useIdleFillMessage(step.label, step.active);

    return (
        <div
            className={cn('relative', isNew && 'animate-slide-up')}
            style={
                isNew
                    ? {
                          animationDelay: `${index * 40}ms`,
                          animationFillMode: 'both'
                      }
                    : undefined
            }
        >
            <div className="flex items-start gap-2 min-w-0">
                <div className="flex justify-center items-center w-6.5 shrink-0">
                    <div className="flex justify-center items-center bg-muted/20 border border-border rounded-full w-6 h-6">
                        <span className="font-medium text-muted-foreground text-xs">
                            {index + 1}
                        </span>
                    </div>
                </div>
                <p
                    className={cn(
                        'min-w-0 text-sm wrap-break-word leading-5',
                        step.active ? 'loading-shimmer' : 'text-foreground'
                    )}
                    title={step.label}
                >
                    {displayLabel}
                </p>
            </div>
            {!isLast && (
                <div
                    className="left-3.25 absolute bg-border w-px"
                    style={{ top: '1.5rem', bottom: `-${ROW_GAP_REM}rem` }}
                />
            )}
        </div>
    );
}

export function ThinkingStepsList({ steps }: Props) {
    const initialCountRef = useRef(steps.length);
    const scrollRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        scrollRef.current?.scrollTo({
            top: scrollRef.current.scrollHeight,
            behavior: 'smooth'
        });
    }, [steps.length]);

    if (!steps.length) return null;

    return (
        <div className="mt-4 border border-border rounded-lg overflow-hidden">
            <div
                ref={scrollRef}
                className="flex flex-col gap-3 p-3 max-h-[200px] overflow-y-auto"
            >
                {steps.map((step, index) => (
                    <ThinkingStepRow
                        key={index}
                        step={step}
                        index={index}
                        isLast={index === steps.length - 1}
                        isNew={index >= initialCountRef.current}
                    />
                ))}
            </div>
        </div>
    );
}
